"use client"

import { useState, useTransition } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  type LucideIcon,
} from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { Button } from "@workspace/ui/components/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form"
import { cn } from "@workspace/ui/lib/utils"

import { checkDeliveryCoverageAction } from "@/app/[locale]/(store)/cart/actions"
import { setOrderTypeAction } from "@/app/[locale]/(store)/order-type-actions"
import { Link, useRouter } from "@/i18n/navigation"
import type {
  DeliveryCoverageResult,
  DeliveryCoverageStatus,
} from "@/lib/delivery-coverage"

export interface ShowcaseCoverageLabels {
  postcode: string
  placeholder: string
  submit: string
  required: string
  failed: string
  /** Modal title per answer. */
  titles: Record<DeliveryCoverageStatus, string>
  orderNow: string
  orderCollection: string
  tryAnother: string
  close: string
}

/** Icon and tint per answer, for the modal's badge. */
const ANSWER_ICONS: Record<
  DeliveryCoverageStatus,
  { Icon: LucideIcon; className: string }
> = {
  ok: {
    Icon: CircleCheckIcon,
    className: "bg-emerald-500/15 text-emerald-500",
  },
  out_of_zone: {
    Icon: CircleXIcon,
    className: "bg-destructive/15 text-destructive",
  },
  unknown: { Icon: InfoIcon, className: "bg-primary/15 text-primary" },
  collection_only: { Icon: InfoIcon, className: "bg-primary/15 text-primary" },
  invalid: {
    Icon: CircleAlertIcon,
    className: "bg-destructive/15 text-destructive",
  },
}

/**
 * The "Can we serve you online?" box's postcode form: one field and a
 * "Find Me!" pill, after the reference. shadcn `Form` (react-hook-form + zod)
 * for the inline "enter your postcode" message; the server action
 * (`checkDeliveryCoverageAction`) answers and remembers the postcode for
 * cart and checkout.
 *
 * The answer opens in a modal so it can't be missed, with the next step for
 * it: "Order Now!" when the store delivers there (or will confirm at
 * checkout); "Order for collection" (switches the order type, then opens the
 * menu) or "Try another postcode" when it doesn't. The last answer also stays
 * under the form; `initial` is the answer for the postcode the diner already
 * gave (this box or `/cart`), so a returning diner sees it there — without
 * the modal popping on page load.
 */
export function ShowcaseCoverageForm({
  locale,
  initial,
  collects,
  labels,
}: {
  locale: string
  initial: DeliveryCoverageResult | null
  /** The store also takes collection orders — offers it for an out-of-zone postcode. */
  collects: boolean
  labels: ShowcaseCoverageLabels
}) {
  const router = useRouter()
  const [result, setResult] = useState(initial)
  const [open, setOpen] = useState(false)
  const [switching, startSwitching] = useTransition()
  const form = useForm<{ postcode: string }>({
    resolver: zodResolver(
      z.object({
        postcode: z.string().trim().min(1, labels.required).max(16),
      })
    ),
    defaultValues: { postcode: initial?.postcode ?? "" },
  })

  async function onSubmit({ postcode }: { postcode: string }) {
    const formData = new FormData()
    formData.set("locale", locale)
    formData.set("postcode", postcode)
    try {
      setResult(await checkDeliveryCoverageAction(formData))
    } catch {
      setResult({ status: "invalid", message: labels.failed })
    }
    setOpen(true)
  }

  function orderForCollection() {
    startSwitching(async () => {
      const formData = new FormData()
      formData.set("orderType", "pickup")
      await setOrderTypeAction(formData)
      setOpen(false)
      router.push("/menu")
    })
  }

  const answer = result ? ANSWER_ICONS[result.status] : null

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-1.5"
      >
        <FormField
          control={form.control}
          name="postcode"
          render={({ field }) => (
            <FormItem className="items-center gap-1">
              <FormLabel className="sr-only">{labels.postcode}</FormLabel>
              <div className="flex items-center gap-1.5">
                <FormControl>
                  <input
                    {...field}
                    autoComplete="postal-code"
                    placeholder={labels.placeholder}
                    className="h-8 w-[154px] bg-white px-0.5 text-center font-display text-[15pt] text-black uppercase outline-none placeholder:text-neutral-400 focus-visible:ring-2 focus-visible:ring-white/70"
                  />
                </FormControl>
                <button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="h-8 rounded-[15px] bg-primary px-2.5 font-display text-[13pt] whitespace-nowrap text-primary-foreground shadow-[inset_0_-2px_0_rgb(0_0_0/0.25)] hover:bg-primary/85 disabled:opacity-70"
                >
                  {labels.submit}
                </button>
              </div>
              <FormMessage className="text-center text-red-300" />
            </FormItem>
          )}
        />
        <div
          id="store-coverage-result"
          aria-live="polite"
          data-status={result?.status}
          className="empty:hidden"
        >
          {result && answer ? (
            <div
              className={cn(
                "flex max-w-[280px] items-start gap-2 text-start text-sm leading-snug",
                result.status === "ok" && "text-emerald-300",
                (result.status === "out_of_zone" ||
                  result.status === "invalid") &&
                  "text-red-300"
              )}
            >
              <answer.Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
              <p>
                <span className="block font-display text-[13pt] leading-tight">
                  {labels.titles[result.status]}
                </span>
                <span className="opacity-90">{result.message}</span>
              </p>
            </div>
          ) : null}
        </div>
      </form>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          // "Try another postcode" / closing on a miss: straight back to
          // the field.
          if (!next && result?.status !== "ok") form.setFocus("postcode")
        }}
      >
        {result && answer ? (
          <DialogContent
            id="store-coverage-dialog"
            data-status={result.status}
            className="text-center sm:max-w-sm"
          >
            <DialogHeader className="items-center gap-3">
              <span
                aria-hidden
                className={cn(
                  "grid size-14 place-items-center rounded-full",
                  answer.className
                )}
              >
                <answer.Icon className="size-8" />
              </span>
              <DialogTitle className="font-display text-[18pt] leading-tight font-normal">
                {labels.titles[result.status]}
              </DialogTitle>
              <DialogDescription className="text-base text-balance">
                {result.message}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-center">
              {result.status === "ok" || result.status === "unknown" ? (
                <>
                  <DialogClose asChild>
                    <Button variant="outline">{labels.close}</Button>
                  </DialogClose>
                  <Button asChild>
                    <Link href="/menu" onClick={() => setOpen(false)}>
                      {labels.orderNow}
                    </Link>
                  </Button>
                </>
              ) : result.status === "out_of_zone" ? (
                <>
                  <DialogClose asChild>
                    <Button variant="outline">{labels.tryAnother}</Button>
                  </DialogClose>
                  {collects ? (
                    <Button onClick={orderForCollection} disabled={switching}>
                      {labels.orderCollection}
                    </Button>
                  ) : null}
                </>
              ) : (
                <DialogClose asChild>
                  <Button>{labels.close}</Button>
                </DialogClose>
              )}
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </Form>
  )
}
